import { Box } from "@mui/joy";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import { colorTokens } from "../../../../styles/globalStyles";
import { GoogleIcon, StarIcon } from "../../../../styles/StyledMUI";
import type { MailBodyBlock } from "../mailBodyBlocks";
import {
  MAIL_GOOGLE_REVIEW_FIELD_KEY,
  mailPlaceholderValueTypesForGoogleReview,
} from "../mailGoogleReviewBlock";
import {
  mailLinePartsForSubjectField,
  parsePlainStringToMailLineParts,
  serializeMailLinePartsToSubject,
} from "../mailLineParts";
import MailRichLineEditor from "./MailRichLineEditor";

type GoogleReviewBlock = Extract<MailBodyBlock, { type: "googleReview" }>;

const STAR_COUNT = 5;

type MailGoogleReviewBlockFieldsProps = {
  block: GoogleReviewBlock;
  canEdit: boolean;
  onPatch: (b: MailBodyBlock) => void;
  setFieldRef: (key: string, el: HTMLElement | null) => void;
  trackFocus: (blockId: string, field: string) => void;
  presentablePhLabel: (key: string) => string;
  placeholderCatalog: MailPlaceholderDefinition[];
};

export default function MailGoogleReviewBlockFields(props: MailGoogleReviewBlockFieldsProps) {
  const {
    block,
    canEdit,
    onPatch,
    setFieldRef,
    trackFocus,
    presentablePhLabel,
    placeholderCatalog,
  } = props;
  const parts = mailLinePartsForSubjectField(parsePlainStringToMailLineParts(block.href));

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colorTokens.bg.surfaceMuted,
          color: "#4285F4",
          flexShrink: 0,
        }}
        aria-hidden
      >
        <GoogleIcon sx={{ fontSize: 22 }} />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          color: "#FBBC04",
          flexShrink: 0,
        }}
        aria-hidden
      >
        {Array.from({ length: STAR_COUNT }, (_, i) => (
          <StarIcon key={i} sx={{ fontSize: 18 }} />
        ))}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
        <MailRichLineEditor
          parts={parts}
          onChange={(nextParts) =>
            onPatch({ ...block, href: serializeMailLinePartsToSubject(nextParts) })
          }
          canEdit={canEdit}
          blockId={block.id}
          editorFieldId={MAIL_GOOGLE_REVIEW_FIELD_KEY}
          ariaLabel="Lien Google Avis"
          setFieldRef={setFieldRef}
          trackFocus={trackFocus}
          variant="subject"
          minRowsText={1}
          presentablePhLabel={presentablePhLabel}
          placeholderCatalog={placeholderCatalog}
          placeholderValueTypes={mailPlaceholderValueTypesForGoogleReview()}
        />
      </Box>
    </Box>
  );
}
